import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import FrequentlyAskedQuestions, {
  FrequentlyAskedQuestion,
} from '@rover/components/src/FrequentlyAskedQuestions';
import { Heading, Link, Paragraph } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { PageLayout } from '@rover/react-lib/src/components/layouts';
import catWithDog from '@rover/react-lib/src/images/policies/guarantee/rp_lp_doodle_cat_with_dog.jpg';
import inject from '@rover/utilities/injector';

import { StyledBulletedList } from '../brand2ColorStyles.constants';

import Ellipse from './images/ellipse.svg';

const { guaranteeTosUrl, tosUrl } = inject('Rover.context', {});

const FAQSection = (): JSX.Element => {
  const { text, space } = useTheme();

  const getListItemsCovered = [
    {
      id: '1',
      IconClass: Ellipse,
      title: t`Vet care for injuries to a pet owner's pet(s) while under a pet care provider's care (in cases where the pet care provider may be otherwise liable).`,
      content: '',
      hasBorder: false,
    },
    {
      id: '2',
      IconClass: Ellipse,
      title: t` Vet care for injuries to the pet care provider’s resident pet(s) caused by a pet owner’s pet(s) (in cases where the pet owner may be otherwise liable).`,
      content: '',
      hasBorder: false,
    },
    {
      id: '3',
      IconClass: Ellipse,
      title: t` Physical damage to a pet owner’s personal property caused by the pet care provider.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '4',
      IconClass: Ellipse,
      title: t` Out-of-pocket medical costs arising from injury to certain third parties caused by a pet owner’s pet(s).`,
      content: '',
      hasBorder: false,
    },
  ];

  const getListItemsNotCovered = [
    {
      id: '1',
      IconClass: Ellipse,
      title: t`Damage to the property of a pet care provider or someone related to or residing with the pet care provider.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '2',
      IconClass: Ellipse,
      title: t`Damage to a pet owner’s property caused by their own pets.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '3',
      IconClass: Ellipse,
      title: t`Injury or damage that arises outside of the dates of a service booked and paid for on the platform, including Meet & Greets.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '4',
      IconClass: Ellipse,
      title: t`Long-term vet care—i.e., care that extends more than 30 days past injury.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '5',
      IconClass: Ellipse,
      title: t`Treatment costs for preventive care, a preventable illness, or a condition that is chronic, breed-specific, or pre-existing.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '6',
      IconClass: Ellipse,
      title: t`Preventable conditions such as fleas, ticks, or parasites.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '7',
      IconClass: Ellipse,
      title: t`Injury to the pet care provider or pet owner or someone related to or residing with the pet care provider or pet owner.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '8',
      IconClass: Ellipse,
      title: t`Consequential, emotional, or other non-economic damages related to an injury or incident.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '9',
      IconClass: Ellipse,
      title: t`Losses and damages paid by any other insurance including health insurance, pet insurance, homeowners insurance, or renters insurance.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '10',
      IconClass: Ellipse,
      title: t`The Rover Guarantee does not provide coverage for theft. For any situation potentially including stolen property, involved parties are encouraged to reach out to local law enforcement.`,
      content: '',
      hasBorder: false,
    },
    {
      id: '11',
      IconClass: Ellipse,
      title: (
        <Paragraph size="200">
          <Link href={guaranteeTosUrl}>
            <Trans>See Rover Guarantee Terms for more details</Trans>
          </Link>
        </Paragraph>
      ),
      content: '',
      hasBorder: false,
    },
  ];

  const QUESTIONS_AND_ANSWERS = [
    {
      question: t`Is the Rover Guarantee an insurance plan?`,
      answer: (
        <>
          <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }}>
            <Trans>
              No. Reimbursement under the Rover Guarantee is generally paid out of Rover’s own
              pocket—though we reserve the right to refer a claim to our insurance carriers when
              necessary or appropriate.
            </Trans>
          </Paragraph>
          <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              The Rover Guarantee exists to fill the gap when the responsible party—usually the pet
              care provider—is unable or unwilling to pay for costs arising from damage or injury
              for which they are legally responsible, and when insurance is not available. It is a
              contractual agreement from Rover to protect its users, enforceable in accordance with
              the Rover Guarantee Terms and Conditions.
            </Trans>
          </Paragraph>
        </>
      ),
    },
    {
      question: t`How long does the reimbursement claims process take?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            The process may vary. We'll work as quickly as possible with hopes of resolving all
            claims within 14 days of receiving all appropriate documentation.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`If I file a claim, am I eligible for a refund?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            Refunds and reimbursement are considered on a case-by-case basis. When it comes to
            refunds for services, we require that owners and pet care providers attempt to work
            together before filing a reimbursement claim. Please try contacting the other party
            through your inbox on the platform as your first step.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`What happens to my account if I file a reimbursement claim?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            We're here for both pet owners and pet care providers, and we take concerns from both
            parties seriously. Safety is a top priority for us, and results for parties found at
            fault can range from coaching to removal from the community. We'll always do our best to
            come to a fair resolution.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`What else is and is not covered?`,
      answer: (
        <>
          <Heading textColor="primary" size="200" mb="6x" mt="4x">
            <Trans>What is covered:</Trans>
          </Heading>
          <StyledBulletedList
            pl="6px"
            pr="6px"
            fontSize={text['200'].fontSize}
            fontWeight={text['200'].fontWeight}
            height={space['6x']}
            items={getListItemsCovered}
          />
          <Heading textColor="primary" size="200" mb="6x" mt="6x">
            <Trans>What isn’t covered:</Trans>
          </Heading>
          <StyledBulletedList
            fontSize={text['200'].fontSize}
            fontWeight={text['200'].fontWeight}
            height={space['6x']}
            items={getListItemsNotCovered}
          />
        </>
      ),
    },
    {
      question: t`Are there other conditions, exclusions, or limitations?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            Yes. For a complete understanding of the Rover Guarantee, including the full list of
            conditions, exclusions and limitations, we strongly encourage you to read the{' '}
            <Link href={tosUrl}>Rover Guarantee Terms and Conditions</Link>
          </Trans>
        </Paragraph>
      ),
    },
  ];

  return (
    <PageLayout style={{ margin: '0 auto' }} data-testid="faq-section">
      <FrequentlyAskedQuestions
        imageSrc={catWithDog}
        imageWidth={320}
        description="Dog with plant image"
      >
        <FrequentlyAskedQuestions.Header>
          <Trans>FAQs</Trans>
        </FrequentlyAskedQuestions.Header>

        <FrequentlyAskedQuestions.Subheader>
          <Trans>Frequently asked questions about the Rover Guarantee</Trans>
        </FrequentlyAskedQuestions.Subheader>

        {QUESTIONS_AND_ANSWERS.map(({ question, answer }, idx) => (
          <FrequentlyAskedQuestion isOpen={idx === 0} key={idx} question={question}>
            {answer}
          </FrequentlyAskedQuestion>
        ))}
      </FrequentlyAskedQuestions>
    </PageLayout>
  );
};

export default FAQSection;
